import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  InputGroup,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";

// redux toolkit
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { fetchUser, updateUser } from "../../slices/apps/userSlice";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";

// react select
import Select from "react-select";

const getSelectStyles = (validation, fieldName) => ({
  control: (base, state) => ({
    ...base,
    borderColor:
      validation.touched[fieldName] && validation.errors[fieldName]
        ? "#e30a17"
        : state.isFocused
        ? "#80bdff"
        : "#e9ebec",
  }),
});

// react input mask
import { IMaskInput } from "react-imask";

const UpdateModal = ({ id, isOpen, toggle }) => {
  const dispatch = useDispatch();

  const { user, userLoading, updateUserLoading, roles } = useSelector(
    (state) => {
      return {
        user: state.user.user,
        userLoading: state.user.userLoading,
        updateUserLoading: state.user.updateUserLoading,
        roles: state.user?.roles,
      };
    },
    shallowEqual
  );

  const fetchData = async () => {
    try {
      await dispatch(fetchUser(id)).unwrap();
      validation.resetForm();
    } catch (error) {
      toggle();
    }
  };

  useEffect(() => {
    if (isOpen && id) {
      fetchData();
    }
  }, [isOpen, id, dispatch]);

  const roleOptions = useMemo(() => {
    if (!roles) {
      return [];
    }
    return roles.map((role) => ({
      value: role.id,
      label: role.name,
    }));
  }, [roles]);

  // validation
  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      id: id,
      nameSurname: user?.nameSurname,
      phoneNumber: user?.phoneNumber,
      roleId: user?.roleId,
      isActive: user?.isActive,
    },

    validationSchema: Yup.object({
      nameSurname: Yup.string()
        .max(100, "Ad Soyad en fazla 100 karakter olabilir.")
        .required("Lütfen ad ve soyad giriniz."),
      phoneNumber: Yup.string()
        .max(20, "Telefon numarası en fazla 20 karakter olabilir.")
        .required("Lütfen telefon numarası giriniz."),
      roleId: Yup.string().required("Lütfen bir rol seçiniz."),
    }),

    onSubmit: async (values) => {
      await dispatch(updateUser(values)).unwrap();
      toggle(false);
    },
  });

  if (userLoading) {
    return null;
  }

  return (
    <Modal size="lg" isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader
        className="modal-title"
        id="myLargeModalLabel"
        toggle={toggle}
      >
        Kullanıcı Bilgilerini Düzenle
      </ModalHeader>

      <ModalBody className="p-4">
        <Form onSubmit={validation.handleSubmit} autoComplete="off">
          <Row className="gy-3">
            <Col lg={6}>
              <Label>Ad Soyad</Label>
              <Input
                type="text"
                name="nameSurname"
                placeholder="Can Yılmaz"
                maxLength={100}
                value={validation.values.nameSurname}
                onChange={validation.handleChange}
                invalid={
                  !!(
                    validation.touched.nameSurname &&
                    validation.errors.nameSurname
                  )
                }
              />
              <FormFeedback>{validation.errors.nameSurname}</FormFeedback>
            </Col>

            <Col lg={6}>
              <Label>Telefon Numarası</Label>
              <InputGroup>
                <span
                  className="input-group-text"
                  style={{
                    width: "44px",
                    justifyContent: "center",
                  }}
                >
                  0
                </span>
                <IMaskInput
                  inputMode="numeric"
                  mask="000 000 00 00"
                  unmask={true}
                  name="phoneNumber"
                  placeholder="5XX XXX XX XX"
                  value={validation.values.phoneNumber}
                  className={`form-control ${
                    validation.touched.phoneNumber &&
                    validation.errors.phoneNumber
                      ? "is-invalid"
                      : ""
                  }`}
                  style={{ zIndex: 0 }}
                  onAccept={(value) =>
                    validation.setFieldValue("phoneNumber", value)
                  }
                />
              </InputGroup>
              {validation.touched.phoneNumber &&
                validation.errors.phoneNumber && (
                  <div className="invalid-feedback d-block">
                    {validation.errors.phoneNumber}
                  </div>
                )}
            </Col>

            <Col lg={12}>
              <Label>Rol</Label>
              <Select
                name="roleId"
                placeholder="Rol Seçiniz..."
                options={roleOptions}
                menuPlacement="auto"
                value={
                  roleOptions.find(
                    (opt) => opt.value === validation.values.roleId
                  ) || null
                }
                onChange={(option) =>
                  validation.setFieldValue("roleId", option ? option.value : "")
                }
                styles={getSelectStyles(validation, "roleId")}
              />
              {validation.touched.roleId && validation.errors.roleId && (
                <div className="invalid-feedback d-block">
                  {validation.errors.roleId}
                </div>
              )}
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button
              type="button"
              color="primary"
              onClick={() => validation.handleSubmit()}
              className="btn btn-label"
              disabled={updateUserLoading}
            >
              <i className="ri-check-line label-icon align-middle me-2"></i>{" "}
              Kaydet
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default UpdateModal;
